import React from 'react';
import useTheme from '@hero/branding/theme';
import Container from '@hero/ui-kit/layout/Container';
import P from '@hero/ui-kit/typography/P';
import CareManagerBadge from '../components/CareManagerBadge';
import { GetCheckinMembersParams } from '../api/useGetCheckinMembers';
import { writeMemberListSnapshot } from '../OrganizationMemberDetails/MemberDetailsWrapper/useMemberListNeighbors';
import getMemberUrl from '../utils/getMemberUrl';
import * as Style from './style.module.scss';
import HighlightedText from '../../../components/HighlightedText';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFormattedDate, parseToDate } from '../../../utils/date';
import CheckinStatusPill from '../components/CheckinStatusPill';
import AutoScheduled from './icons/AutoScheduled';
import ManagerScheduled from './icons/ManagerScheduled';
import MemberMismatchAlert from '../components/MemberMismatchAlert';
import { CheckinColumnId } from './columns';

/** Humanize the notification_phase enum (e.g. first_month_primary -> First Month Primary). */
const getNotificationPhaseLabel = (phase?: string | null): string =>
    phase
        ? phase
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
        : '-';

const ListItem: React.FC<{
    memberId?: number | null;
    heroUserId?: number | null;
    name?: string;
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    deviceFirstName?: string | null;
    deviceLastName?: string | null;
    deviceDateOfBirth?: string | null;
    email?: string;
    search: string;
    canOpenDetails?: boolean;
    activatedAt?: string | null;
    externalUserId?: string | null;
    careManagerIds?: number[] | null;
    status?: string | null;
    nextCheckinDate?: string | null;
    isAutoScheduled?: boolean;
    scheduledCheckinDate?: string | null;
    notificationPhase?: string | null;
    listQuery: GetCheckinMembersParams;
    isColumnVisible: (id: CheckinColumnId) => boolean;
    gridColumns: string;
}> = ({
    name,
    firstName,
    lastName,
    dateOfBirth,
    deviceFirstName,
    deviceLastName,
    deviceDateOfBirth,
    heroUserId,
    memberId,
    email,
    search,
    externalUserId,
    careManagerIds,
    canOpenDetails = false,
    activatedAt,
    status,
    nextCheckinDate,
    isAutoScheduled = true,
    scheduledCheckinDate,
    notificationPhase,
    listQuery,
    isColumnVisible,
    gridColumns
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const hasId: boolean = React.useMemo(() => {
        return !!memberId || !!heroUserId;
    }, [memberId, heroUserId]);

    const handleNavigate = React.useCallback(
        (e: React.SyntheticEvent) => {
            if (!e.isDefaultPrevented() && hasId && canOpenDetails) {
                const from = `${location.pathname}${location.search}`;
                const state = { from, listQuery, listKind: 'checkin-members' as const };
                writeMemberListSnapshot(state);

                memberId
                    ? navigate(`${getMemberUrl(null, memberId)}`, { state })
                    : navigate(`${getMemberUrl(heroUserId, memberId)}`, { state });
            }
        },
        [hasId, memberId, heroUserId, navigate, canOpenDetails, location, listQuery]
    );

    return (
        <div
            onClick={handleNavigate}
            onKeyDown={() => null}
            tabIndex={0}
            role="button"
            style={{ cursor: hasId ? 'pointer' : 'not-allowed' }}
            className={Style.memberListWrapper}
        >
            <Container
                gridTemplateColumns={gridColumns}
                gridColumnGap="small"
                className={Style.container}
            >
                <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'start',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                    >
                        <P noDefaultMargin strong>
                            <HighlightedText highlight={search} text={name} />
                        </P>
                        {email ? (
                            <P
                                size="small"
                                noDefaultMargin
                                styles={{
                                    color: theme.colors.neutrals.textGamma
                                }}
                            >
                                <HighlightedText highlight={search} text={email} />
                            </P>
                        ) : (
                            ''
                        )}
                    </div>
                    {isColumnVisible('patientId') && (
                        <P noDefaultMargin styles={{ display: 'flex', alignItems: 'center' }}>
                            {externalUserId}
                        </P>
                    )}
                    {isColumnVisible('activatedAt') && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    color: theme.colors.neutrals.textGamma
                                }}
                            >
                                {activatedAt
                                    ? getFormattedDate(parseToDate(activatedAt), 'MMM dd, yyyy')
                                    : '-'}
                            </P>
                        </div>
                    )}

                    {isColumnVisible('careManager') && (
                        <CareManagerBadge careManagerIds={careManagerIds ?? null} />
                    )}

                    {isColumnVisible('checkinStatus') && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <CheckinStatusPill status={status} />
                        </div>
                    )}
                    {isColumnVisible('currentCheckinDate') && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: '1rem'
                            }}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    color: theme.colors.neutrals.textGamma
                                }}
                                strong
                            >
                                {nextCheckinDate
                                    ? getFormattedDate(
                                          parseToDate(nextCheckinDate),
                                          'MMM dd, yyyy HH:mm'
                                      )
                                    : '-'}
                            </P>
                            {isAutoScheduled ? (
                                <div title="Default Scheduled">
                                    <AutoScheduled />
                                </div>
                            ) : (
                                <div title="Manager Scheduled">
                                    <ManagerScheduled />
                                </div>
                            )}
                        </div>
                    )}
                    {isColumnVisible('scheduledDate') && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <P noDefaultMargin styles={{ color: theme.colors.neutrals.textGamma }}>
                                {scheduledCheckinDate
                                    ? getFormattedDate(
                                          parseToDate(scheduledCheckinDate),
                                          'MMM dd, yyyy HH:mm'
                                      )
                                    : '-'}
                            </P>
                        </div>
                    )}
                    {isColumnVisible('notificationPhase') && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <P noDefaultMargin>{getNotificationPhaseLabel(notificationPhase)}</P>
                        </div>
                    )}
                    <MemberMismatchAlert
                        firstName={firstName}
                        lastName={lastName}
                        dateOfBirth={dateOfBirth}
                        deviceFirstName={deviceFirstName}
                        deviceLastName={deviceLastName}
                        deviceDateOfBirth={deviceDateOfBirth}
                    />
                </>
            </Container>
        </div>
    );
};

export default ListItem;
